import * as _ from 'lodash-es';
import React, { useState } from 'react';
import type { PresenceAvatarStatus } from './PresenceAvatar';
import PresenceAvatar from './PresenceAvatar';
import * as styles from './PresencePopOver.module.scss';
import useLegacyIsLoggedIn from '../account/useIsLoggedIn';
import useAppSelector from '../app/redux/useAppSelector';
import Tooltip from '../components/Tooltip';
import type { PresenceStateUser } from '../editor/editorSlice';
import UserDetailPreviewPopOver from '../user/UserDetailPreviewPopOver';
import Link from '../utils/router/Link';
import userPath from '../utils/url/userPath';
import useIsPublicView from '../utils/useIsPublicView';

interface PresenceUserRowNameProps {
  name: string;
  tooltipIfTruncated: boolean;
}

const PresenceUserRowName = ({
  name,
  tooltipIfTruncated,
}: PresenceUserRowNameProps) => {
  const [nameElement, setNameElement] = useState<HTMLSpanElement | null>(null);

  const isTruncated =
    tooltipIfTruncated &&
    nameElement &&
    nameElement.scrollWidth > nameElement.offsetWidth;

  const inner = (
    <span ref={setNameElement} className={styles.name}>
      {name}
    </span>
  );

  return isTruncated ? (
    <Tooltip content={name} display="inline">
      {inner}
    </Tooltip>
  ) : (
    inner
  );
};

interface PresenceUserRowProps {
  user: PresenceStateUser;
  status: PresenceAvatarStatus;
  subTitle?: string;
}

const PresenceUserRow = ({ user, status, subTitle }: PresenceUserRowProps) => {
  const isPublicView = useIsPublicView();
  const inner = (
    <>
      <PresenceAvatar status={status} index={user.index} user={user} />
      <div className={styles.userText}>
        <PresenceUserRowName
          name={user.name}
          tooltipIfTruncated={isPublicView}
        />
        <span className={styles.subtitle}>{subTitle}</span>
      </div>
    </>
  );
  if (isPublicView) {
    return <div className={styles.userRow}>{inner}</div>;
  }
  return (
    <UserDetailPreviewPopOver
      userId={user.id}
      placement="left"
      hoverDelay="fast"
    >
      <Link className={styles.userRow} to={userPath(user)} newTab>
        {inner}
      </Link>
    </UserDetailPreviewPopOver>
  );
};

interface PresenceUsersSectionProps {
  users: PresenceStateUser[];
  status: PresenceAvatarStatus;
}

const PresenceUsersSection = ({ users, status }: PresenceUsersSectionProps) =>
  users.length > 0 ? (
    <>
      <h3 className={styles.title}>{_.capitalize(status)}</h3>
      {users.map((user) => (
        <PresenceUserRow key={user.id} user={user} status={status} />
      ))}
    </>
  ) : null;

interface PresencePopOverProps {
  users: {
    user: PresenceStateUser;
    status: PresenceAvatarStatus;
  }[];
}

const PresencePopOver = ({ users }: PresencePopOverProps) => {
  const isPublicView = useIsPublicView();
  const isLoggedIn = useLegacyIsLoggedIn();
  const currentUser = useAppSelector((state) => state.session.user);

  const currentPresenceVisitor =
    !isLoggedIn && currentUser
      ? users.find((u) => u.user.id === currentUser.id)
      : null;

  const {
    editing = [],
    reading = [],
    inactive = [],
  } = _.groupBy(users, ({ status }) => status);

  return (
    <div className={styles.container}>
      {isPublicView && currentPresenceVisitor && (
        <>
          <PresenceUserRow
            user={currentPresenceVisitor.user}
            status={currentPresenceVisitor.status}
            subTitle="You"
          />
          {users.length > 0 && <div className={styles.separator} />}
        </>
      )}
      <PresenceUsersSection
        status="editing"
        users={editing.map(({ user }) => user)}
      />
      {editing.length > 0 && reading.length > 0 && (
        <div className={styles.separator} />
      )}
      <PresenceUsersSection
        status="reading"
        users={reading.map(({ user }) => user)}
      />
      {(editing.length > 0 || reading.length > 0) && inactive.length > 0 && (
        <div className={styles.separator} />
      )}
      <PresenceUsersSection
        status="inactive"
        users={inactive.map(({ user }) => user)}
      />
    </div>
  );
};

export default PresencePopOver;
